/**
 * @description
 * Stripe customer link component for linking clients to Stripe customers.
 * Allows creating new Stripe customers or linking existing ones.
 *
 * @author AI Developer <ai@byteable.ai>
 * @date 2025-01-08
 * @see admin/src/frontend/components/stripe/StripeCustomerLink.test.tsx
 */

import { useState, useEffect, useCallback } from 'react';
import { toast } from 'react-hot-toast';
import { api } from '../../services/api';

interface StripeCustomer {
  id: string;
  clientId: string;
  stripeCustomerId: string;
  email: string;
  name: string;
  currency: string;
  defaultPaymentMethod: string | null;
  createdAt: string;
  updatedAt: string;
}

interface StripeCustomerLinkProps {
  clientId: string;
}

/**
 * Stripe customer link component
 */
export function StripeCustomerLink({ clientId }: StripeCustomerLinkProps) {
  const [customer, setCustomer] = useState<StripeCustomer | null>(null);
  const [loading, setLoading] = useState(true);
  const [creating, setCreating] = useState(false);
  const [linking, setLinking] = useState(false);
  const [showLinkForm, setShowLinkForm] = useState(false);

  // Form state
  const [customerEmail, setCustomerEmail] = useState('');
  const [customerName, setCustomerName] = useState('');
  const [existingCustomerId, setExistingCustomerId] = useState('');

  const loadData = useCallback(async () => {
    try {
      setLoading(true);
      
      // Load client data
      const clientData = await api.getClient(clientId);
      setCustomerEmail(`${clientData.subdomain}@byteable.ai`);
      setCustomerName(clientData.name);

      // Load Stripe customer data
      try {
        const customerData = await api.getStripeCustomer(clientId);
        setCustomer(customerData);
      } catch {
        // Customer doesn't exist yet - this is expected for new clients
        setCustomer(null);
      }
    } catch (error) {
      console.error('Error loading data:', error);
      toast.error('Failed to load client data');
    } finally {
      setLoading(false);
    }
  }, [clientId]);

  useEffect(() => {
    loadData();
  }, [loadData]);

  const handleCreateCustomer = async () => {
    if (!customerEmail || !customerName) {
      toast.error('Please fill in all required fields');
      return;
    }

    try {
      setCreating(true);
      
      const newCustomer = await api.createStripeCustomer(clientId, {
        email: customerEmail,
        name: customerName,
      });

      setCustomer(newCustomer);
      setShowLinkForm(false);
      toast.success('Stripe customer created successfully');
    } catch (error) {
      console.error('Error creating Stripe customer:', error);
      toast.error('Failed to create Stripe customer');
    } finally {
      setCreating(false);
    }
  };

  const handleLinkCustomer = async () => {
    if (!existingCustomerId) {
      toast.error('Please enter a Stripe customer ID');
      return;
    }

    try {
      setLinking(true);
      
      await api.linkStripeCustomer(clientId, existingCustomerId);
      
      // Reload customer data
      const customerData = await api.getStripeCustomer(clientId);
      setCustomer(customerData);
      setShowLinkForm(false);
      setExistingCustomerId('');
      
      toast.success('Stripe customer linked successfully');
    } catch (error) {
      console.error('Error linking Stripe customer:', error);
      toast.error('Failed to link Stripe customer');
    } finally {
      setLinking(false);
    }
  };

  const handleUnlinkCustomer = async () => {
    if (!confirm('Are you sure you want to unlink this Stripe customer? This will disable billing for this client.')) {
      return;
    }

    try {
      // For now, we'll just clear the local state
      // In a full implementation, this would call an API to unlink the customer
      setCustomer(null);
      toast.success('Stripe customer unlinked successfully');
    } catch (error) {
      console.error('Error unlinking Stripe customer:', error);
      toast.error('Failed to unlink Stripe customer');
    }
  };

  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleDateString();
  };

  if (loading) {
    return (
      <div className="rounded-lg border bg-card p-6">
        <div className="flex items-center justify-center py-8">
          <div className="h-6 w-6 animate-spin rounded-full border-2 border-primary border-t-transparent"></div>
        </div>
      </div>
    );
  }

  return (
    <div className="rounded-lg border bg-card p-6">
      <div className="flex items-center justify-between mb-4">
        <h3 className="text-lg font-semibold">Stripe Customer</h3>
        {!customer && (
          <button
            onClick={() => setShowLinkForm(!showLinkForm)}
            className="rounded-md border border-input bg-background px-3 py-2 text-sm font-medium transition-colors hover:bg-accent"
          >
            {showLinkForm ? 'Cancel' : 'Link Customer'}
          </button>
        )}
      </div>

      {customer ? (
        <div className="space-y-4">
          {/* Customer Info */}
          <div className="grid gap-4 md:grid-cols-2">
            <div>
              <label className="text-sm font-medium text-muted-foreground">Customer ID</label>
              <div className="mt-1 text-sm font-mono bg-muted px-3 py-2 rounded-md">
                {customer.stripeCustomerId}
              </div>
            </div>
            <div>
              <label className="text-sm font-medium text-muted-foreground">Email</label>
              <div className="mt-1 text-sm bg-muted px-3 py-2 rounded-md">
                {customer.email}
              </div>
            </div>
            <div>
              <label className="text-sm font-medium text-muted-foreground">Name</label>
              <div className="mt-1 text-sm bg-muted px-3 py-2 rounded-md">
                {customer.name}
              </div>
            </div>
            <div>
              <label className="text-sm font-medium text-muted-foreground">Currency</label>
              <div className="mt-1 text-sm bg-muted px-3 py-2 rounded-md">
                {customer.currency.toUpperCase()}
              </div>
            </div>
          </div>

          {/* Status */}
          <div className="flex items-center gap-2">
            <div className="h-2 w-2 rounded-full bg-green-500"></div>
            <span className="text-sm text-green-600">Linked to Stripe</span>
          </div>

          {/* Dates */}
          <div className="grid gap-4 md:grid-cols-2">
            <div>
              <label className="text-sm font-medium text-muted-foreground">Created</label>
              <div className="mt-1 text-sm">
                {formatDate(customer.createdAt)}
              </div>
            </div>
            <div>
              <label className="text-sm font-medium text-muted-foreground">Last Updated</label>
              <div className="mt-1 text-sm">
                {formatDate(customer.updatedAt)}
              </div>
            </div>
          </div>

          {/* Actions */}
          <div className="flex gap-2 pt-4 border-t">
            <button
              onClick={handleUnlinkCustomer}
              className="rounded-md border border-red-200 bg-background px-4 py-2 text-sm font-medium text-red-600 hover:bg-red-50"
            >
              Unlink Customer
            </button>
            <a
              href={`https://dashboard.stripe.com/customers/${customer.stripeCustomerId}`}
              target="_blank"
              rel="noopener noreferrer"
              className="rounded-md border border-input bg-background px-4 py-2 text-sm font-medium transition-colors hover:bg-accent"
            >
              View in Stripe
            </a>
          </div>
        </div>
      ) : (
        <div className="space-y-4">
          {/* No Customer State */}
          <div className="text-center py-8">
            <svg className="h-12 w-12 text-muted-foreground mx-auto mb-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z" />
            </svg>
            <h4 className="text-lg font-medium mb-2">No Stripe Customer</h4>
            <p className="text-sm text-muted-foreground mb-4">
              This client is not linked to a Stripe customer. Create a new customer or link an existing one to enable billing.
            </p>
          </div>

          {/* Link Form */}
          {showLinkForm && (
            <div className="space-y-4 p-4 border rounded-md bg-muted/50">
              <div className="flex gap-2">
                <button
                  onClick={() => setShowLinkForm(false)}
                  className="flex-1 rounded-md border border-input bg-background px-4 py-2 text-sm font-medium transition-colors hover:bg-accent"
                >
                  Create New Customer
                </button>
                <button
                  onClick={() => setShowLinkForm(false)}
                  className="flex-1 rounded-md border border-input bg-background px-4 py-2 text-sm font-medium transition-colors hover:bg-accent"
                >
                  Link Existing Customer
                </button>
              </div>

              {/* Create New Customer Form */}
              <div className="space-y-3">
                <div>
                  <label className="text-sm font-medium">Email Address</label>
                  <input
                    type="email"
                    value={customerEmail}
                    onChange={(e) => setCustomerEmail(e.target.value)}
                    className="mt-1 w-full rounded-md border border-input bg-background px-3 py-2 text-sm"
                    placeholder="customer@example.com"
                  />
                </div>
                <div>
                  <label className="text-sm font-medium">Customer Name</label>
                  <input
                    type="text"
                    value={customerName}
                    onChange={(e) => setCustomerName(e.target.value)}
                    className="mt-1 w-full rounded-md border border-input bg-background px-3 py-2 text-sm"
                    placeholder="Customer Name"
                  />
                </div>
                <button
                  onClick={handleCreateCustomer}
                  disabled={creating || !customerEmail || !customerName}
                  className="w-full rounded-md bg-primary px-4 py-2 text-sm font-medium text-primary-foreground hover:bg-primary/90 disabled:opacity-50"
                >
                  {creating ? 'Creating...' : 'Create Stripe Customer'}
                </button>
              </div>

              {/* Link Existing Customer Form */}
              <div className="space-y-3 border-t pt-4">
                <div>
                  <label className="text-sm font-medium">Stripe Customer ID</label>
                  <input
                    type="text"
                    value={existingCustomerId}
                    onChange={(e) => setExistingCustomerId(e.target.value)}
                    className="mt-1 w-full rounded-md border border-input bg-background px-3 py-2 text-sm font-mono"
                    placeholder="cus_xxxxxxxxxxxxx"
                  />
                  <p className="text-xs text-muted-foreground mt-1">
                    Enter the Stripe customer ID (starts with "cus_")
                  </p>
                </div>
                <button
                  onClick={handleLinkCustomer}
                  disabled={linking || !existingCustomerId}
                  className="w-full rounded-md border border-input bg-background px-4 py-2 text-sm font-medium transition-colors hover:bg-accent disabled:opacity-50"
                >
                  {linking ? 'Linking...' : 'Link Existing Customer'}
                </button>
              </div>
            </div>
          )}
        </div>
      )}
    </div>
  );
}

/*
 * © 2025 Byte Inc. All rights reserved.
 * This code is the confidential and proprietary information of Byte Inc.
 * You shall not disclose such Confidential Information and shall use it only in
 * accordance with the terms of the license agreement you entered into with Byte Inc.
 */
